import React, { useEffect, Suspense, useRef } from 'react'
import gsap from 'gsap';
import { graphql } from 'gatsby';
import { GatsbyImage, StaticImage, getSrc } from 'gatsby-plugin-image';
import { SplitText } from 'gsap/SplitText';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styled from 'styled-components'
import TransitionLink from 'gatsby-plugin-transition-link';
import { FooterContent, ScrollDownIndicator } from './../components'
import Seo from './../components/Seo';
import { Heading, HalfWidthHeading, ParagraphSica, ParagraphSmall, ParagraphWrap, H2, H3 } from '../components/Typography'
import Page from './../containers/Page'
import t from '../data/en_new.json'
import arrowTopRight from './../images/arrow-topright-pointing.svg';

// import { Canvas, extend, useFrame } from '@react-three/fiber'
// import { PerspectiveCamera } from '@react-three/drei'

import {
  ActivityElement,
  ActivityGrid,
  ActivityTextContainer,
  GhostButton
} from '../components'

import {
  Content,
  FullWidthSectionNoTopMargin,
  CustomerJourneySection,
  ImageSwitchSection,
  ImageSectionPicture,
  ImageSectionText,
  ImageSectionTitle,
  ImageSectionParagraph,
  StayLongerSection,
} from '../components/Subpage'

// import SkyDome from './../components/Map/Sky'
// import { Water as WaterMesh } from './../examples/Water'

gsap.registerPlugin(SplitText, ScrollTrigger);

const HeroSectionView = styled.section`
  display: grid;
  width: 100%;
  height: 100vh;
  color: #ffffff;
  background-color: #041E42;
  position: fixed;
  top: 0;
  left: 0;
  z-index: -1;

  video {
    grid-area: 1/1;
    object-fit: cover;
    width: 100vw;
    height: 100vh;
    filter: brightness(75%);
  }
`

const HeroContentWrapper = styled.header`
  display: grid;
  grid-area: 1/1;
  position: relative;
  place-items: center;
`

const TextHolder = styled.section`
  max-width: 640px;
  text-align: ${({ textAlign }) => textAlign || 'center'};
  font-size: 19px;
  color: #ffffff;
  margin: 0 36px;
  text-align: center;

  @media screen and (min-width: 768px) {
    margin: 0;
  }
`

const TextHolderNoMargin = styled(TextHolder)`
  margin: 0;
  text-align: ${({ textAlign }) => textAlign || 'center'};
`;

const YearsText = styled.p`
  opacity: 0;
`

const Headline = styled.h1`
  opacity: 0;
  font-size: 55px;
  font-weight: 400;
  margin-bottom: 4rem;

  @media screen and (min-width: 769px) {
    font-size: 65px;
  }
`

const MainText = styled.p`
  opacity: 0;
  margin-bottom: 4rem;
  font-family: 'Sica', sans-serif;
  padding: 0;
  text-align: center;

  @media screen and (min-width: 768px) {
    padding: 0 60px;
  }
`

const MainSectionView = styled.section`
  padding-bottom: 8rem;
  position: relative;
  margin-top: 100vh;
  background: linear-gradient(to bottom, transparent 0%, transparent 17%, #fff 17%, #fff 100%);

  &:after{
    content: '';
    background: #ffffff;
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;

    @media screen and (min-width: 768px) {
      top: 50%;
    }
  }
`

const MainSectionContent = styled.div`
  margin: 0 auto;
  max-width: 640px;
  background: rgba(255,255,255,0);
`

const WhiteBG = styled.div`
  background: #ffffff;
  padding: 0;

  @media screen and (min-width: 1024px) {
    padding: 4rem 0;
    padding-top: ${props => props?.topSpacing ? '120px' : '0px'};
  }
`

const EventSchedule = styled.p`
  font-size: 1.0625rem;
  line-height: 1.4;
  margin-top: 16px;
`;

const EventLocation = styled.p`
  font-size: 1.0625rem;
  line-height: 1.4;
  font-family: 'Sica', sans-serif;
`;

const EventLocationSpacing = styled(EventLocation)`
  margin-top: 20px;
`;

const EventNote = styled.p`
  font-family: 'Sica', sans-serif;
  ${({ decorator }) => decorator && 'font-weight: bold;'}
`

const EventView = styled.p`
  margin: ${({ margin }) => margin};
  color: #ffffff;
  & div {
    color: #ffffff;
  }
`

const Ul = styled.ul`
  margin: 0 0 .825rem 3.8rem;
  & li {
    color: #ffffff;
  } 
`

const SpecialCase = styled.div`
  display: inline-flex;
  flex-direction: row;
`

const FullWidthInsetView = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: center;
`

const ContentContainer = styled.div`
  margin: 0 36px;

  @media screen and (min-width: 1024px) {
    width: 100%;
    max-width: 920px;
    margin: 0 auto;
  }
`

const FullImageTextContainer = styled.div`
  @media screen and (min-width: 768px) {
    width: 50%;
  }
`

const ArrowTopRight = styled.img`
  width: 14px;
  height: 14px;
  margin-left: 10px;
`

const FooterView = styled.section`
  height: 100vh;
  width: 100%;
  display: grid;
  color: #ffffff;
  position: relative;
`

const HeadingMain = styled(Heading)`
  margin: 1.825rem 0 2rem;
  @media screen and (min-width: 1024px) {
    margin: .825rem 0 2rem;
  } 
`

const PreHeading = styled.p`
  color: #fff;
  font-size: 1.0625rem;
  margin-bottom: 12px;
  margin-top: 40px;
  @media screen and (min-width: 1024px) {
    max-width: ${({maxHalf}) => maxHalf ? '60%' : '100%'};
  }
`;

// extend({ WaterMesh })

// const Scene = () => {
//   const oceanRef = useRef()

//   useFrame(() => {
//     oceanRef.current.material.uniforms['time'].value += .75 / 60.0
//     oceanRef.current.material.uniforms['size'].value = 4
//   })

//   return <>
//     <PerspectiveCamera
//       fov={75}
//       makeDefault={true}
//       position={[0, 2, 0]}
//       far={10000}
//       near={0.1}
//       zoom={1}
//     />
//     {/* <waterMesh
//       ref={oceanRef}
//       position={[0, 0, 0]}
//       rotation={[-Math.PI / 2, 0, 0]}
//     /> */}
//     <SkyDome />
//   </>
// } 

const PeturLarsen = ({ data }) => {
  const { allFile } = data

  const createHeroTextAnimation = () => {
    const heroSection = document.querySelector('.js-hero-section')
    const welcome = heroSection.querySelector('.js-hero-welcome')
    const anniversary = heroSection.querySelector('.js-hero-anniversary')
    const description = heroSection.querySelector('.js-hero-description')
    const welcomeSplitText = new SplitText(welcome, { type: 'words' })
    const anniversarySplitText = new SplitText(anniversary, { type: 'words' })
    const descriptionSplitText = new SplitText(description, { type: 'lines' })

    gsap.fromTo(welcomeSplitText.words,
      { opacity: 0, x: -8, filter: 'blur(5px)' },
      { opacity: 1, x: 0, filter: 'blur(0px)', stagger: .2, duration: .3, onStart: () => {
        gsap.set(welcome, { opacity: 1 })
      }},
    )
    gsap.fromTo(anniversarySplitText.words,
      { opacity: 0, x: -8, filter: 'blur(7px)' },
      { opacity: 1, x: 0, filter: 'blur(0px)', stagger: .2, delay: .9, duration: .6, onStart: () => {
        gsap.set(anniversary, { opacity: 1 })
      }},
    )
    gsap.set(descriptionSplitText.lines, { css: { textAlign: 'center' }})
    gsap.fromTo(descriptionSplitText.lines,
      { opacity: 0, x: -12, filter: 'blur(5px)' },
      { opacity: 1, x: 0, filter: 'blur(0px)', stagger: .2, delay: 1.7, duration: .3, onStart: () => {
        gsap.set(description, { opacity: 1 })
      }},
    )
  }

  const createScrollAnimation = () => {
    const sections = gsap.utils.toArray('.lift')

    sections.forEach(section => {

      ScrollTrigger.matchMedia({
        '(max-width: 767px)': () => {
          gsap.to(section, { clearProps: 'transform' })
        },
        '(min-width: 768px)': () => {
          const sectionChildren = gsap.utils.toArray(section.childNodes)
          
          sectionChildren.forEach(child => {
            const isImageWrapper = child.classList.contains('js-image-wrapper')
            const textAnimation =  {
              scrollTrigger: {
                trigger: child,
                scrub: true,
                start: 'top 75%',
                end: 'bottom top',
              },
              ease: 'power2.out',
              duration: .5,
              opacity: 0,
              y: -70,
            }
            const imageAnimation =  {
              scrollTrigger: {
                trigger: child,
                scrub: true,
                end: 'bottom 60%',
              },
              duration: 1.2,
              scale: .7,
            }

            gsap.from(child, isImageWrapper ? imageAnimation : textAnimation)
            if (isImageWrapper) {
              const imageElement = child.querySelector('.gatsby-image-wrapper')
              const animation = gsap.fromTo(imageElement, 
                { translateY: 80, ease: 'slow(0.3 0.3, 0.6 0.6, false)' },
                { translateY: -80, ease: 'slow(0.3 0.3, 0.6 0.6, false)' }
              )
              ScrollTrigger.create({
                trigger: child,
                animation: animation,
                start: 'top-=200px bottom',
                end: 'bottom top',
                scrub: true,
              })
            }
          })
        }
      })
    })
  }

  const getImageObject = (fileName, images) => {
    const image = images.filter(image => image.node.childImageSharp.resize.originalName === fileName)
    return image.length > 0 ? image[0].node.childImageSharp.gatsbyImageData : {}
  }

  const getVideoPosterSource = filename => {
    const image = getImageObject(filename, allFile.fullWidth)
    return getSrc(image)
  }

  useEffect(() => {
    createHeroTextAnimation()
    createScrollAnimation()
  }, [])

  return (
    <Page>
      <Seo
        title={t.peturLarsen.HeroTitle}
        description={t.peturLarsen.HeroText}
      />
      <HeroSectionView className="js-hero-section">
        <video loop muted autoPlay playsInline poster={getVideoPosterSource(t.peturLarsen.hero_image.filename)}>
          <source src={t.peturLarsen.hero_image.videoUrl} type="video/mp4" />
        </video>
        <HeroContentWrapper>
          <TextHolder>
            <YearsText className="js-hero-welcome">{t.peturLarsen.HeroWelcome}</YearsText>
            <Headline className="js-hero-anniversary">{t.peturLarsen.HeroTitle}</Headline>
            <MainText className="js-hero-description">{t.peturLarsen.HeroText}</MainText>
          </TextHolder>
        </HeroContentWrapper>
        <ScrollDownIndicator />
      </HeroSectionView>

      <Content transparent={true}>
        <MainSectionView>
          <MainSectionContent>
            <StaticImage
              src="./../images/petur-larsen/portrait.jpg"
              alt="Petur Larsen portrait"
              placeholder="blurred"
              layout="fullWidth"
              loading="lazy"
            />
            <TextHolder>
              <HeadingMain>{t.peturLarsen.MainTitle}</HeadingMain>
              <ParagraphWrap textAlign='left' dangerouslySetInnerHTML={ { __html: t.peturLarsen.MainText }} />
            </TextHolder>
          </MainSectionContent>
        </MainSectionView>

        {t.peturLarsen.sections.slice(0, 2).map((section, index) =>
          <WhiteBG key={index}>
            <ImageSwitchSection className='lift' isEven={index % 2 === 1}>
              <ImageSectionPicture className="js-image-wrapper">
                <GatsbyImage
                  image={getImageObject(section.image.filename, allFile.constrained)}
                  alt={section.image.altText}
                  width={section.image.dimensions.width}
                  height={section.image.dimensions.height}
                  placeholder="blurred"
                  layout="constrained"
                  loading="lazy"
                />
              </ImageSectionPicture>
              <ImageSectionText isEven={index % 2 === 1}>
                <EventSchedule>
                  {section.schedule}
                </EventSchedule>
                <ImageSectionTitle>
                  {section.title}
                </ImageSectionTitle>
                <ImageSectionParagraph>
                  {section.text}
                </ImageSectionParagraph>
                {section.text2?.map(t => (
                  <ImageSectionParagraph dangerouslySetInnerHTML={ { __html: t }} />
                ))}
                <EventLocationSpacing>
                  Location:
                </EventLocationSpacing>
                <EventLocation>
                  {section.location}
                </EventLocation>
              </ImageSectionText>
            </ImageSwitchSection>
          </WhiteBG>
        )}

        <CustomerJourneySection>
          <StaticImage
            src="./../images/petur-larsen/AdamFirman-VisitFaroeIslands.jpg"
            alt="Overview of the islands"
            placeholder="blurred"
            layout="fullWidth"
            loading="lazy"
          />
          <FullWidthInsetView>
            <ContentContainer>
              <PreHeading>
                {t.peturLarsen.customerJourneyPretitle}
              </PreHeading>
              <HalfWidthHeading color="#ffffff">
                {t.peturLarsen.customerJourneyTitle}
              </HalfWidthHeading>
              <PreHeading maxHalf={true}>
                {t.peturLarsen.customerJourneyDescription}
              </PreHeading>
              {t.peturLarsen.customerJourneySchedule.map((event, i) => (
                <EventView margin={event?.reduceMargin ? '0 0 .8em 0' : '0 0 2em 0'}>
                  <div>{event.event}</div>
                  {event.activities && <Ul>
                    {event.activities.map((activity, j) => (
                      <li>{activity}</li>
                    ))}
                  </Ul>}
                  {event.note && <EventNote decorator={event.note?.decorator || false}>{event.note.text}</EventNote>}
                  {event.dresscode && <SpecialCase>
                    <EventNote decorator={event.dresscode?.decorator || false}>{event.dresscode.text1}</EventNote>
                    <EventNote decorator={false}>{event.dresscode.text2}</EventNote>
                    </SpecialCase>
                  }
                </EventView>
              ))}

              <PreHeading>Friday 20th of May 2022, 18:30</PreHeading>
              <HalfWidthHeading color="#ffffff">50 years celebration </HalfWidthHeading>
              <EventView>{t.peturLarsen.customerCelebrationDetails}</EventView>
              <EventView><br/><strong>Dresscode: </strong><span>Casual</span><br/><br/></EventView>
              <EventView>{t.peturLarsen.customerLocation}</EventView>
            </ContentContainer>
          </FullWidthInsetView>
        </CustomerJourneySection>

        {t.peturLarsen.sections.slice(3).map((section, index) =>
          <WhiteBG key={index} topSpacing={index === 0}>
            <ImageSwitchSection className='lift' mobilePadding={true} isEven={index % 2 === 0}>
              <ImageSectionPicture className="js-image-wrapper">
                <GatsbyImage
                  image={getImageObject(section.image.filename, allFile.constrained)}
                  alt={section.image.altText}
                  width={section.image.dimensions.width}
                  height={section.image.dimensions.height}
                  placeholder="blurred"
                  layout="constrained"
                  loading="lazy"
                />
              </ImageSectionPicture>
              <ImageSectionText isEven={index % 2 === 0}>
                <EventSchedule>
                  {section.schedule}
                </EventSchedule>
                <ImageSectionTitle>
                  {section.title}
                </ImageSectionTitle>
                {Boolean(section?.text) && <ImageSectionParagraph>
                  {section.text}
                </ImageSectionParagraph>}
                {section.text2?.map(t => (
                  <>
                    <ImageSectionParagraph dangerouslySetInnerHTML={ { __html: t }} />
                    <br />
                  </>
                ))}
                {section.location && <>
                  <EventLocationSpacing>
                    Location:
                  </EventLocationSpacing>
                  <EventLocation>
                    {section.location}
                  </EventLocation></>
                }
              </ImageSectionText>
            </ImageSwitchSection>
          </WhiteBG>
        )}

        <FullWidthSectionNoTopMargin>
          <StaticImage
            src="./../images/petur-larsen/sunset-with-horses.png"
            alt="Sunset hiking with horses"
            placeholder="blurred"
            layout="fullWidth"
            loading="lazy"
          />
          <FullWidthInsetView>
            <ContentContainer>
              <FullImageTextContainer>
                <Heading color="#ffffff">{t.peturLarsen.attendTitle}</Heading>
                <ParagraphSica color='#ffffff' margin='0 0 2rem 0' whiteSpace='pre-line'>
                  {t.peturLarsen.attendDescription}
                </ParagraphSica>
                <TransitionLink
                  to="/signup"
                  exit={{
                    trigger: ({ entry, node }) => gsap.to(node, { xPercent: -100, duration: .4, ease: 'power3.out', }),
                    length: .4
                  }}
                  entry={{
                    trigger: ({ exit, node }) => gsap.from(node, { xPercent: 100, duration: .4, ease: 'power3.out', }),
                    length: .4
                  }}
                >
                  <GhostButton>
                  {t.peturLarsen.attendCtaLabel}
                  </GhostButton>
                </TransitionLink>
              </FullImageTextContainer>
            </ContentContainer>
          </FullWidthInsetView>
        </FullWidthSectionNoTopMargin>

        <StayLongerSection>
          <ContentContainer>
            <TextHolderNoMargin textAlign='left'>
              <H2>{t.peturLarsen.stayLongerTitle}</H2>
              <ParagraphSica>{t.peturLarsen.stayLongerText}</ParagraphSica>
            </TextHolderNoMargin>
            <ActivityGrid>
              {t.peturLarsen.activities.filter(a => a.type === 0).map((activity, i) =>
                <a href={activity.bookingLink} rel="noreferrer noopener" target="_blank" key={i}>
                  <ActivityElement>
                    <GatsbyImage
                      image={getImageObject(activity.image.filename, allFile.thumbnail)}
                      alt={activity.image.altText}
                      width={activity.image.dimensions.width}
                      height={activity.image.dimensions.height}
                      placeholder="blurred"
                      layout="constrained"
                      loading="lazy"
                    />
                    <ActivityTextContainer>
                      <H3>{activity.title}</H3>
                      <ParagraphSmall margin='1rem 0'>
                        {activity.location}
                      </ParagraphSmall>
                      <ParagraphSmall margin='0 0 1.5rem 0'>
                        {activity.description}
                      </ParagraphSmall>
                      <ParagraphSmall>
                        Get info/booking <ArrowTopRight src={arrowTopRight} alt="Arrow" />
                      </ParagraphSmall>
                    </ActivityTextContainer>
                  </ActivityElement>
                </a>
              )}
            </ActivityGrid>
          </ContentContainer>
        </StayLongerSection>
        
        <FooterView>
          {/* <Canvas style={{ width: '100%', height: '100%' }} >
            <ambientLight intensity={1} color='#ffffff'/>
            <Suspense fallback={'loading...'}>
              <Scene />
            </Suspense>
          </Canvas> */}
          <FooterContent />
        </FooterView>
      </Content>
    </Page>
  )
}

export default PeturLarsen
export const mainEventQuery = graphql`
  query MainEventQuery {
    allFile(filter: {relativeDirectory: {eq: "images/petur-larsen"}}) {
      fullWidth: edges {
        node {
          name
          childImageSharp {
            gatsbyImageData(placeholder: BLURRED, layout: FULL_WIDTH)
            resize {
              originalName
            }
          }
        }
      }
      constrained: edges {
        node {
          name
          childImageSharp {
            gatsbyImageData(placeholder: BLURRED, layout: CONSTRAINED, width: 610, height: 800)
            resize {
              originalName
            }
          }
        }
      }
      thumbnail: edges {
        node {
          name
          childImageSharp {
            gatsbyImageData(placeholder: BLURRED, layout: FIXED, width: 204, height: 204)
            resize {
              originalName
            }
          }
        }
      }
    }
  }
`
